import React, { useState } from 'react';
import { Modal, Button, ReturnReasonModal, NoCostReasonModal, ConfirmModal, Toast, DatePickerPopover } from '../../../shared/components';
import { ShoppingCart, Package, Calendar, Ticket, DollarSign, MapPin, RotateCcw, X, Loader2, Gift, Pencil, Check } from 'lucide-react';
import { formatSeatId } from '../../../shared/utils/seatUtils';
import { useRoles } from '../../../shared/hooks/useRoles';

const ReservationDetails = ({
  isOpen,
  onClose,
  reservation,
  onMarkAsSold,
  onMarkSeatAsSold,
  onMarkAsDelivered,
  onReturnReservation,
  onReturnGroupReservations,
  onCancelReservation,
  onCancelGroupReservations,
  onMarkAsNoCost,
  onUpdateExpiry,
  canMarkAsSold,
  canMarkAsDelivered,
  canReturn,
  canCancel
}) => {
  const { isAdmin } = useRoles();

  // Estados para modal de sin costo
  const [isNoCostModalOpen, setIsNoCostModalOpen] = useState(false);
  const [seatForNoCost, setSeatForNoCost] = useState(null);

  // Estado para marcar entrada individual como vendida
  const [seatForSold, setSeatForSold] = useState(null);

  // Estados para edición de la fecha de vencimiento
  const [isEditingExpiry, setIsEditingExpiry] = useState(false);
  const [expiryInput, setExpiryInput] = useState(null);
  const [savingExpiry, setSavingExpiry] = useState(false);
  // Estados para modal de devolución
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returnAction, setReturnAction] = useState(null); // 'group' | 'individual'
  const [selectedSeat, setSelectedSeat] = useState(null);

  // Estados para modales de confirmación
  const [isCancelGroupModalOpen, setIsCancelGroupModalOpen] = useState(false);
  const [isCancelIndividualModalOpen, setIsCancelIndividualModalOpen] = useState(false);
  const [seatToCancel, setSeatToCancel] = useState(null);
  const [isMarkSeatAsSoldModalOpen, setIsMarkSeatAsSoldModalOpen] = useState(false);
  const [seatToMarkAsSold, setSeatToMarkAsSold] = useState(null);

  // Estados de carga para botones
  const [loadingStates, setLoadingStates] = useState({
    markAsSold: false,
    markSeatAsSold: false,
    markAsDelivered: false,
    returnGroup: false,
    returnIndividual: false,
    cancelGroup: false,
    cancelIndividual: false,
    markAsNoCost: false
  });

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  if (!reservation) return null;

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Actualizar estado de carga
  const setLoading = (action, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [action]: isLoading }));
  };

  // Formatear precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price);
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener color del estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'confirmed':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'sold':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'delivered':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'returned':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'blocked':
        return 'bg-slate-50 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  // Obtener texto del estado
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Reservado';
      case 'confirmed':
        return 'Confirmada';
      case 'sold':
        return 'Vendida';
      case 'delivered':
        return 'Entregada';
      case 'returned':
        return 'Devuelta';
      case 'cancelled':
        return 'Cancelada';
      case 'blocked':
        return 'Bloqueado';
      default:
        return status;
    }
  };


  // Formatear fecha de vencimiento
  const formatExpiryDate = (dateString) => {
    if (!dateString) return 'Sin vencimiento';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Handlers para edición de vencimiento
  const handleStartEditExpiry = () => {
    setExpiryInput(
      reservation.accessCodeExpiresAt ? new Date(reservation.accessCodeExpiresAt) : null
    );
    setIsEditingExpiry(true);
  };

  const handleCancelEditExpiry = () => {
    setIsEditingExpiry(false);
    setExpiryInput(null);
  };

  const handleSaveExpiry = async () => {
    try {
      setSavingExpiry(true);
      await onUpdateExpiry(reservation.accessCodeId, expiryInput ? expiryInput.toISOString() : null);
      showToast('Fecha de vencimiento actualizada exitosamente', 'success');
      setIsEditingExpiry(false);
    } catch (error) {
      showToast(error.message || 'Error al actualizar la fecha de vencimiento', 'error');
    } finally {
      setSavingExpiry(false);
    }
  };

  // Handlers para devoluciones
  const handleReturnGroup = () => {
    setReturnAction('group');
    setSelectedSeat(null);
    setIsReturnModalOpen(true);
  };

  const handleReturnIndividual = (seat) => {
    setReturnAction('individual');
    setSelectedSeat(seat);
    setIsReturnModalOpen(true);
  };

  const handleConfirmReturn = async (reason) => {
    setIsReturnModalOpen(false);
    
    try {
      if (returnAction === 'group') {
        setLoading('returnGroup', true);
        await onReturnGroupReservations(reason);
        showToast('Reservas devueltas exitosamente', 'success');
        onClose(); // Cerrar modal principal
      } else if (returnAction === 'individual' && selectedSeat) {
        setLoading('returnIndividual', true);
        await onReturnReservation(selectedSeat.id, reason);
        showToast('Reserva devuelta exitosamente', 'success');
        onClose(); // Cerrar modal principal
      }
    } catch (error) {
      showToast('Error al devolver la reserva', 'error');
    } finally {
      setLoading(returnAction === 'group' ? 'returnGroup' : 'returnIndividual', false);
      setReturnAction(null);
      setSelectedSeat(null);
    }
  };

  const canReturnSeat = (seat) => {
    return seat.status === 'sold' || seat.status === 'blocked';
  };

  // Handlers para cancelaciones
  const handleCancelGroup = async () => {
    setIsCancelGroupModalOpen(true);
  };

  const handleCancelIndividual = async (seat) => {
    setSeatToCancel(seat);
    setIsCancelIndividualModalOpen(true);
  };

  const confirmCancelGroup = async () => {
    try {
      setLoading('cancelGroup', true);
      await onCancelGroupReservations();
      showToast('Reservas canceladas exitosamente', 'success');
      setIsCancelGroupModalOpen(false);
      onClose(); // Cerrar modal principal
    } catch (error) {
      showToast('Error al cancelar las reservas', 'error');
    } finally {
      setLoading('cancelGroup', false);
    }
  };

  const confirmCancelIndividual = async () => {
    try {
      setLoading('cancelIndividual', true);
      await onCancelReservation(seatToCancel.id);
      showToast('Reserva cancelada exitosamente', 'success');
      setIsCancelIndividualModalOpen(false);
      setSeatToCancel(null);
      onClose(); // Cerrar modal principal
    } catch (error) {
      showToast('Error al cancelar la reserva', 'error');
    } finally {
      setLoading('cancelIndividual', false);
    }
  };

  const canCancelSeat = (seat) => {
    return seat.status === 'pending';
  };

  // Handlers para marcar entrada individual como vendida
  const canMarkSeatAsSold = (seat) => {
    return seat.status === 'pending' || seat.status === 'confirmed';
  };

  const handleMarkSeatAsSold = (seat) => {
    setSeatToMarkAsSold(seat);
    setIsMarkSeatAsSoldModalOpen(true);
  };

  const confirmMarkSeatAsSold = async () => {
    const seat = seatToMarkAsSold;
    try {
      setSeatForSold(seat);
      setLoading('markSeatAsSold', true);
      await onMarkSeatAsSold(seat.id);
      showToast('Entrada marcada como vendida exitosamente', 'success');
      setIsMarkSeatAsSoldModalOpen(false);
    } catch (error) {
      showToast(error.message || 'Error al marcar la entrada como vendida', 'error');
    } finally {
      setLoading('markSeatAsSold', false);
      setSeatForSold(null);
      setSeatToMarkAsSold(null);
    }
  };

  // Handlers para marcar sin costo
  const canMarkSeatAsNoCost = (seat) => {
    return isAdmin && !seat.isFreeOfCharge && seat.status !== 'cancelled' && seat.status !== 'returned';
  };

  const handleMarkSeatAsNoCost = (seat) => {
    setSeatForNoCost(seat);
    setIsNoCostModalOpen(true);
  };

  const handleConfirmNoCost = async (reason) => {
    setIsNoCostModalOpen(false);

    try {
      setLoading('markAsNoCost', true);
      await onMarkAsNoCost(seatForNoCost.id, reason);
      showToast('Entrada marcada sin costo exitosamente', 'success');
    } catch (error) {
      showToast(error.message || 'Error al marcar la entrada sin costo', 'error');
    } finally {
      setLoading('markAsNoCost', false);
      setSeatForNoCost(null);
    }
  };

  // Handlers para marcar como vendida y entregada
  const handleMarkAsSold = async () => {
    try {
      setLoading('markAsSold', true);
      await onMarkAsSold();
      showToast('Reservas marcadas como vendidas exitosamente', 'success');
      onClose(); // Cerrar modal principal
    } catch (error) {
      showToast('Error al marcar como vendidas', 'error');
    } finally {
      setLoading('markAsSold', false);
    }
  };

  const handleMarkAsDelivered = async () => {
    try {
      setLoading('markAsDelivered', true);
      await onMarkAsDelivered();
      showToast('Reservas marcadas como entregadas exitosamente', 'success');
      onClose(); // Cerrar modal principal
    } catch (error) {
      showToast('Error al marcar como entregadas', 'error');
    } finally {
      setLoading('markAsDelivered', false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalles de la Reserva"
      size="lg"
    >
      <div className="space-y-6">

        {/* Información principal */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mx-auto mb-3">
                <DollarSign className="w-6 h-6 text-gray-600" />
              </div>
              <p className="text-sm font-medium text-gray-500 mb-1">Saldo a Pagar</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(
                  reservation.seats
                    ?.filter(seat => seat.status === 'pending')
                    ?.reduce((total, seat) => total + (parseFloat(seat.price) || 0), 0) || 0
                )}
              </p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mx-auto mb-3">
                <Ticket className="w-6 h-6 text-gray-600" />
              </div>
              <p className="text-sm font-medium text-gray-500 mb-1">Total de Asientos</p>
              <p className="text-2xl font-bold text-gray-900">{reservation.totalSeats}</p>
            </div>
            
            
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mx-auto mb-3">
                <Calendar className="w-6 h-6 text-gray-600" />
              </div>
              <p className="text-sm font-medium text-gray-500 mb-1">Fecha de Reserva</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(reservation.createdAt)}</p>
            </div>
          </div>

          {reservation.accessCodeId && (
            <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-500 mr-2">Vencimiento del código:</span>
                {isEditingExpiry ? (
                  <div className="flex items-center space-x-2">
                    <DatePickerPopover
                      value={expiryInput}
                      onChange={setExpiryInput}
                      placeholder="Sin vencimiento"
                    />
                    <button
                      onClick={handleSaveExpiry}
                      disabled={savingExpiry}
                      className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                      title="Guardar"
                    >
                      {savingExpiry ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={handleCancelEditExpiry}
                      disabled={savingExpiry}
                      className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      title="Cancelar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className="text-sm font-medium text-gray-900">{formatExpiryDate(reservation.accessCodeExpiresAt)}</span>
                )}
              </div>
              {isAdmin && !isEditingExpiry && (
                <button
                  onClick={handleStartEditExpiry}
                  className="text-blue-600 hover:text-blue-700 p-1 rounded"
                  title="Cambiar fecha de vencimiento"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Detalle de asientos */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <MapPin className="w-5 h-5 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Asientos Reservados</h3>
          </div>
          
          <div className="space-y-3">
            {reservation.seats.map((seat, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900 mb-1">
                    {formatSeatId(seat.compositeId)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Precio: {formatPrice(seat.price)}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {seat.isFreeOfCharge && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-teal-50 text-teal-700 border-teal-200">
                      Sin costo
                    </span>
                  )}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(seat.status)}`}>
                    {getStatusText(seat.status)}
                  </span>
                  {canMarkSeatAsSold(seat) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkSeatAsSold(seat)}
                      disabled={loadingStates.markSeatAsSold}
                      className="text-emerald-600 hover:text-emerald-700 border-emerald-300 hover:border-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Marcar esta entrada como vendida"
                    >
                      {loadingStates.markSeatAsSold && seatForSold?.id === seat.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <ShoppingCart className="w-3 h-3" />
                      )}
                    </Button>
                  )}
                  {canMarkSeatAsNoCost(seat) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkSeatAsNoCost(seat)}
                      disabled={loadingStates.markAsNoCost}
                      className="text-teal-600 hover:text-teal-700 border-teal-300 hover:border-teal-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Marcar esta entrada sin costo"
                    >
                      {loadingStates.markAsNoCost && seatForNoCost?.id === seat.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Gift className="w-3 h-3" />
                      )}
                    </Button>
                  )}
                  {canReturnSeat(seat) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReturnIndividual(seat)}
                      disabled={loadingStates.returnIndividual}
                      className="text-orange-600 hover:text-orange-700 border-orange-300 hover:border-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Devolver esta entrada"
                    >
                      {loadingStates.returnIndividual ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3 h-3" />
                      )}
                    </Button>
                  )}
                  {canCancelSeat(seat) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelIndividual(seat)}
                      disabled={loadingStates.cancelIndividual}
                      className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Cancelar esta reserva"
                    >
                      {loadingStates.cancelIndividual ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <X className="w-3 h-3" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Acciones */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cerrar
          </Button>
          
          {canMarkAsSold && (
            <Button
              onClick={handleMarkAsSold}
              disabled={loadingStates.markAsSold}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingStates.markAsSold ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShoppingCart className="w-4 h-4 mr-2" />
              )}
              {loadingStates.markAsSold ? 'Procesando...' : 'Marcar como Vendida'}
            </Button>
          )}
          
          {canMarkAsDelivered && (
            <Button
              onClick={handleMarkAsDelivered}
              disabled={loadingStates.markAsDelivered}
              className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingStates.markAsDelivered ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Package className="w-4 h-4 mr-2" />
              )}
              {loadingStates.markAsDelivered ? 'Procesando...' : 'Marcar como Entregada'}
            </Button>
          )}
          
          {canReturn && canReturn(reservation) && (
            <Button
              onClick={handleReturnGroup}
              disabled={loadingStates.returnGroup}
              className="bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingStates.returnGroup ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {loadingStates.returnGroup ? 'Procesando...' : 'Devolver Reservas'}
            </Button>
          )}
          
          {canCancel && canCancel(reservation) && (
            <Button
              onClick={handleCancelGroup}
              disabled={loadingStates.cancelGroup}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingStates.cancelGroup ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              {loadingStates.cancelGroup ? 'Procesando...' : 'Cancelar Reservas'}
            </Button>
          )}
        </div>
      </div>

      {/* Modal de devolución con motivo */}
      <ReturnReasonModal
        isOpen={isReturnModalOpen}
        onClose={() => setIsReturnModalOpen(false)}
        onConfirm={handleConfirmReturn}
        title="Devolver Reserva"
        description={
          returnAction === 'group'
            ? reservation?.overallStatus === 'blocked'
              ? `¿Estás seguro de que quieres devolver todas las entradas bloqueadas para este evento?`
              : `¿Estás seguro de que quieres devolver todas las entradas de ${reservation?.studentName}?`
            : returnAction === 'individual' && selectedSeat
            ? `¿Estás seguro de que quieres devolver la entrada ${formatSeatId(selectedSeat.compositeId)}?`
            : ''
        }
        type={returnAction}
      />

      {/* Modal de sin costo con motivo */}
      <NoCostReasonModal
        isOpen={isNoCostModalOpen}
        onClose={() => {
          setIsNoCostModalOpen(false);
          setSeatForNoCost(null);
        }}
        onConfirm={handleConfirmNoCost}
        description={
          seatForNoCost
            ? `¿Marcar la entrada ${formatSeatId(seatForNoCost.compositeId)} como sin costo?`
            : ''
        }
      />

      {/* Modales de confirmación */}
      <ConfirmModal
        isOpen={isCancelGroupModalOpen}
        onClose={() => setIsCancelGroupModalOpen(false)}
        onConfirm={confirmCancelGroup}
        title="Confirmar Cancelación"
        message={`¿Estás seguro de que quieres cancelar todas las reservas de ${reservation?.studentName}?`}
        type="danger"
        confirmText="Cancelar Reservas"
        cancelText="No Cancelar"
      />

      <ConfirmModal
        isOpen={isCancelIndividualModalOpen}
        onClose={() => setIsCancelIndividualModalOpen(false)}
        onConfirm={confirmCancelIndividual}
        title="Confirmar Cancelación"
        message={`¿Estás seguro de que quieres cancelar la reserva del asiento ${seatToCancel ? formatSeatId(seatToCancel.compositeId) : ''}?`}
        type="danger"
        confirmText="Cancelar Reserva"
        cancelText="No Cancelar"
      />

      <ConfirmModal
        isOpen={isMarkSeatAsSoldModalOpen}
        onClose={() => {
          setIsMarkSeatAsSoldModalOpen(false);
          setSeatToMarkAsSold(null);
        }}
        onConfirm={confirmMarkSeatAsSold}
        title="Confirmar Venta"
        message={`¿Estás seguro de que quieres marcar como vendida la entrada ${seatToMarkAsSold ? formatSeatId(seatToMarkAsSold.compositeId) : ''}?`}
        type="warning"
        confirmText="Marcar como Vendida"
        cancelText="Cancelar"
      />

      {/* Notificaciones */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </Modal>
  );
};

export default ReservationDetails; 